/* eslint-disable @heidi/rules/no-new-legacy-session-data -- Not session data: the v3 upload transport's own delivery bookkeeping. This module already owned the chunk-response stream; the note-generation gate reads which of this page's uploads it has seen persisted. No session record, key or API is added, and the stream state cannot leave this module without moving the transport. */
import { isTranscribeChunkV3Enabled } from '@/features/flags';
// The dependency-free cached reader — not `useQueryUser`, whose import graph
// (scribeAPIV2, flags side effects) this module must not drag into the
// provider, and whose legacy path the identity-projection ratchet freezes.
import { getCurrentUser } from '@/hooks/query/current-user-cache';
import { getAuthTokenForHttpClientSync } from '@/utils/http/getAuthTokenForHttpClientSync';
import { buildScribeSseHeaders } from '@/utils/http/scribeSseHeaders';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { fetchEventSource } from '@fortaine/fetch-event-source';
import { deserializeBlob, setRecordingUploadTransport } from '@heidi/capture';
import {
  type Format,
  type RecordingUploadTransport,
  type SessionChunk,
  type TemporalUploadTicket,
} from '@heidi/capture/contracts';
import { TRANSCRIPTION_SERVICE_TRANSCRIPTS_QUERY_KEY } from '@heidi/session/contracts';
import {
  ChunkResponseSchema,
  DeliveryProgressSchema,
  clearChunkResponsePreviews,
  isChunkResponsePreviewQuery,
  recordChunkResponsePreview,
  recordPersistedChunkResponse,
} from '@heidi/session/data';
import axios, { AxiosError } from 'axios';
import { round } from 'lodash-es';
import pRetry from 'p-retry';
import { ZodError } from 'zod';
import {
  ChunkTicketResponseSchema,
  chunkMetadataFields,
  chunkObjectName,
  stopMarkerMetadataFields,
  stopMarkerObjectName,
} from './chunkTicket';
import {
  getChunkTicketsBaseUrl,
  getTranscriptionBaseUrl,
  t11nClient,
} from './t11nClient';

/**
 * v3: chunks upload direct-to-storage under a per-delivery ticket. The transport
 * pinned on the ledger recording row routes every later chunk, not this module's
 * flag; transcript results arrive over SSE.
 *
 * This module and `chunkTicket.ts` author the wire contract the consumer
 * decodes, so any other client opting into v3 must mirror it byte-for-byte.
 */

/**
 * Bounds a stalled mint tightly: recording start awaits it AFTER the UI
 * already shows RECORDING, so every second here is silently unrecorded audio.
 * Keep the direct ticket request bounded; do not retry it on the startup path.
 */
const MINT_TIMEOUT_MS = 3_000;

/**
 * Bounds a stalled upload so the attempt rejects into pRetry instead of
 * wedging the completion fence; generous — chunks are seconds of audio.
 */
const SIGNED_FORM_TIMEOUT_MS = 60_000;
const TRANSCRIPT_REFRESH_TIMEOUT_MS = 30_000;

/** Re-mint ahead of expiry so no POST starts under a dying ticket. */
const TICKET_REFRESH_MARGIN_MS = 90_000;

type TicketRefreshReason = 'expiring' | 'rejected';

type TemporalUploadTransport = Extract<RecordingUploadTransport, { kind: 'temporal' }>;

class TransportPinChangedError extends Error {}

const inFlightRefreshes = new Map<string, Promise<TemporalUploadTransport>>();

export const isChunkUploadV3Active = (): boolean =>
  Boolean(getChunkTicketsBaseUrl()) && isTranscribeChunkV3Enabled();

/**
 * `prepareUploadTransport` port body: mint and pin a temporal transport on the
 * just-created ledger recording row. Best-effort — any failure logs and leaves
 * the recording on the HTTP path.
 */
export const prepareChunkUploadV3 = async (
  sessionId: string,
  recordingId: string
): Promise<void> => {
  if (!isChunkUploadV3Active()) return;
  const deliveryId = crypto.randomUUID();
  let uploadTransport: TemporalUploadTransport;
  try {
    uploadTransport = {
      kind: 'temporal',
      deliveryId,
      startedAt: Date.now(),
      ticket: await mintTicket(sessionId, recordingId, deliveryId),
    };
  } catch (err) {
    // A schema mismatch is FE/BE contract drift breaking 100% of mints —
    // never let it hide among transient network warns.
    const contractDrift = err instanceof ZodError;
    logger[contractDrift ? 'error' : 'warn']({
      tag: contractDrift ? 'v3_ticket_contract_mismatch' : 'v3_ticket_mint_failed',
      event_type: contractDrift ? 'V3_TICKET_CONTRACT_MISMATCH' : 'V3_TICKET_MINT_FAILED',
      details: {
        session_id: sessionId,
        recording_id: recordingId,
        status: err instanceof AxiosError ? err.response?.status : undefined,
        // Validator messages can include signed ticket fields.
        error_type: err instanceof Error ? err.name : 'UnknownError',
      },
    });
    return;
  }
  try {
    const result = await setRecordingUploadTransport(
      { sessionId, recordingId },
      uploadTransport
    );
    if (result === 'rejected') {
      // A fresh mint found a different delivery already pinned — the one-run-
      // per-recording invariant broke somewhere upstream. The pinned delivery
      // still routes uploads; this is the canary, not the failure.
      logger.error({
        tag: 'v3_delivery_reuse_detected',
        event_type: 'V3_DELIVERY_REUSE_DETECTED',
        details: {
          session_id: sessionId,
          recording_id: recordingId,
          attempted_delivery_id: deliveryId,
        },
      });
    } else if (result !== 'pinned') {
      // Unpinned means this recording silently falls back to HTTP. A store
      // that refused the write is loud; an absent row is not ours to fix.
      logger[result === 'failed' ? 'error' : 'warn']({
        tag: 'v3_transport_not_pinned',
        event_type: 'V3_TRANSPORT_NOT_PINNED',
        details: {
          session_id: sessionId,
          recording_id: recordingId,
          attempted_delivery_id: deliveryId,
          result,
        },
      });
    }
  } catch (err) {
    // The mint succeeded but the pin write failed — the recording stays on
    // HTTP; loud because a fence/storage fault here is never routine.
    logger.error({
      tag: 'v3_transport_pin_failed',
      event_type: 'V3_TRANSPORT_PIN_FAILED',
      details: {
        session_id: sessionId,
        recording_id: recordingId,
        error: err instanceof Error ? err.message : String(err),
      },
    });
  }
};

/**
 * Throws so processChunkV2's pRetry owns re-attempts; a rejected or expiring
 * ticket re-mints once for the same delivery first. Tiny chunks upload too — the
 * consumer requires a gapless 0..last_chunk_index to finalize, so nothing is
 * skipped client-side.
 */
export const uploadChunkV3 = async (
  chunk: SessionChunk,
  format: Format,
  uploadTransport: TemporalUploadTransport,
  languages: string[],
  signal?: AbortSignal
): Promise<void> => {
  const { sessionId, recordingId, index, duration, producedAt, blob: serialized } = chunk;
  if (!serialized) throw new Error('chunk blob missing');
  if (!recordingId) throw new Error('chunk recording id missing');
  const blob = deserializeBlob(serialized)!;

  // Replays reach here without a mint in this page load — explicit re-arm.
  const responseStream = startChunkResponses(sessionId, recordingId, uploadTransport);
  if (responseStream.lastChunkIndex !== null) {
    responseStream.lastChunkIndex = Math.max(responseStream.lastChunkIndex, index);
  }

  // The consumer rejects duration_ms=0 (gt=0), which would drop the chunk and
  // gap the delivery — floor a sub-millisecond flush chunk instead.
  const durationMs = Math.max(1, round(duration * 1000));
  const metadata = chunkMetadataFields({
    startOffsetMs: Math.max(
      0,
      round(producedAt - durationMs - uploadTransport.startedAt)
    ),
    durationMs,
    languages,
    country: getCurrentUser()?.country ?? undefined,
  });
  const objectName = chunkObjectName(index, format);

  const transport = await renewExpiringTicket(
    sessionId,
    recordingId,
    uploadTransport,
    signal
  );

  try {
    await postSignedForm(requireTicket(transport), objectName, metadata, blob, signal);
  } catch (err) {
    if (!isTicketRejection(err)) throw err;
    const refreshed = await refreshTemporalTransport(
      sessionId,
      recordingId,
      transport,
      'rejected',
      signal
    );
    await postSignedForm(requireTicket(refreshed), objectName, metadata, blob, signal);
  }
  responseStream.uploadedIndices.add(index);
  responseStream.transcriptWaitAbandoned = false;
  notifyTranscriptProgress();
};

/** Chunks inside the boundary that settled without uploading. */
export interface DeliveryGaps {
  failed: number;
  abandoned: number;
}

/** Whether the stop marker reached storage; the finalize attempt reads it. */
export type StopMarkerOutcome = 'posted' | 'failed';

/** Post the stop marker; failure logs only — inactivity still finalizes. */
export const finishChunkUploadV3 = async (
  sessionId: string,
  recordingId: string,
  uploadTransport: TemporalUploadTransport,
  lastChunkIndex: number | null,
  gaps?: DeliveryGaps
): Promise<StopMarkerOutcome> => {
  if (gaps && gaps.failed + gaps.abandoned > 0) {
    // The marker still posts — the consumer finalizes PARTIAL and a later
    // replay can upgrade it — but a knowingly gapped delivery must be loud.
    logger.error({
      tag: 'v3_delivery_gapped_at_stop',
      event_type: 'V3_DELIVERY_GAPPED_AT_STOP',
      details: {
        session_id: sessionId,
        recording_id: recordingId,
        delivery_id: uploadTransport.deliveryId,
        last_chunk_index: lastChunkIndex,
        failed: gaps.failed,
        abandoned: gaps.abandoned,
      },
    });
  }
  const responseStream = startChunkResponses(sessionId, recordingId, uploadTransport);
  if (lastChunkIndex !== null) {
    responseStream.lastChunkIndex = Math.max(
      responseStream.lastChunkIndex ?? -1,
      lastChunkIndex
    );
  }
  // Rebound on refresh so a later attempt never restarts from a ticket
  // already known to be expired.
  let currentTransport = uploadTransport;
  try {
    if (lastChunkIndex === null) {
      throw new Error('recording completed without a last chunk index');
    }
    await pRetry(
      async () => {
        const metadata = stopMarkerMetadataFields(lastChunkIndex);
        currentTransport = await renewExpiringTicket(
          sessionId,
          recordingId,
          currentTransport
        );
        try {
          await postSignedForm(
            requireTicket(currentTransport),
            stopMarkerObjectName(),
            metadata,
            undefined
          );
        } catch (err) {
          if (!isTicketRejection(err)) throw err;
          currentTransport = await refreshTemporalTransport(
            sessionId,
            recordingId,
            currentTransport,
            'rejected'
          );
          await postSignedForm(
            requireTicket(currentTransport),
            stopMarkerObjectName(),
            metadata,
            undefined
          );
        }
      },
      { retries: 3 }
    );
    return 'posted';
  } catch (err) {
    logger.error({
      tag: 'v3_stop_marker_failed',
      event_type: 'V3_STOP_MARKER_FAILED',
      details: {
        session_id: sessionId,
        recording_id: recordingId,
        delivery_id: uploadTransport.deliveryId,
        last_chunk_index: lastChunkIndex,
        error_type: err instanceof Error ? err.name : 'UnknownError',
      },
    });
    return 'failed';
  } finally {
    responseStream.stopAfter = Date.now() + 30 * 60_000;
    closeCompletedResponseStream(responseStream);
  }
};

/** Same-delivery re-mint; concurrent callers share one mint and one pin. */
const refreshTemporalTransport = (
  sessionId: string,
  recordingId: string,
  previous: TemporalUploadTransport,
  reason: TicketRefreshReason,
  signal?: AbortSignal
): Promise<TemporalUploadTransport> => {
  const key = `${recordingId}:${previous.deliveryId}`;
  const inFlight = inFlightRefreshes.get(key);
  if (inFlight) return inFlight;
  const refresh = mintAndPinTransport(sessionId, recordingId, previous, reason, signal);
  inFlightRefreshes.set(key, refresh);
  const cleanup = () => {
    if (inFlightRefreshes.get(key) === refresh) inFlightRefreshes.delete(key);
  };
  void refresh.then(cleanup, cleanup);
  return refresh;
};

const mintAndPinTransport = async (
  sessionId: string,
  recordingId: string,
  previous: TemporalUploadTransport,
  reason: TicketRefreshReason,
  signal?: AbortSignal
): Promise<TemporalUploadTransport> => {
  const previousTicket = requireTicket(previous);
  const ticket = await mintTicket(sessionId, recordingId, previous.deliveryId, signal);
  if (ticket.keyPrefix !== previousTicket.keyPrefix) {
    throw new Error('ticket prefix changed during refresh');
  }
  if (ticket.url !== previousTicket.url) {
    throw new Error('ticket upload target changed during refresh');
  }
  const uploadTransport: TemporalUploadTransport = {
    ...previous,
    ticket,
  };
  const result = await setRecordingUploadTransport(
    { sessionId, recordingId },
    uploadTransport
  );
  if (result !== 'pinned') {
    // The row changed under the refresh (a different delivery pinned, or the
    // row vanished). Uploading under this ticket would orphan the chunk —
    // fail the attempt; the next one re-reads the row's current transport.
    logger.error({
      tag: 'v3_transport_changed_mid_refresh',
      event_type: 'V3_TRANSPORT_CHANGED_MID_REFRESH',
      details: {
        session_id: sessionId,
        recording_id: recordingId,
        attempted_delivery_id: previous.deliveryId,
        result,
      },
    });
    throw new TransportPinChangedError(`transport pin ${result} during ticket refresh`);
  }
  logger.info({
    tag: 'v3_ticket_refreshed',
    event_type: 'V3_TICKET_REFRESHED',
    details: {
      session_id: sessionId,
      recording_id: recordingId,
      delivery_id: previous.deliveryId,
      reason,
    },
  });
  return uploadTransport;
};

const mintTicket = async (
  sessionId: string,
  recordingId: string,
  deliveryId: string,
  signal?: AbortSignal
): Promise<TemporalUploadTicket> => {
  const baseUrl = getChunkTicketsBaseUrl();
  if (!baseUrl) throw new Error('chunk ticket listener is not configured');
  const url = `${baseUrl}/v1/chunk-upload-tickets`;
  const body = {
    session_id: sessionId,
    recording_id: recordingId,
    delivery_id: deliveryId,
  };
  const { data } = await (signal
    ? t11nClient.post<unknown>(url, body, { signal, timeout: MINT_TIMEOUT_MS })
    : t11nClient.post<unknown>(url, body, { timeout: MINT_TIMEOUT_MS }));
  const response = ChunkTicketResponseSchema.parse(data);
  return {
    url: response.url,
    fields: response.fields,
    keyPrefix: response.key_prefix,
    expiresAt: new Date(response.expires_at).getTime(),
  };
};

const requireTicket = (
  uploadTransport: TemporalUploadTransport
): TemporalUploadTicket => {
  if (!uploadTransport.ticket) throw new Error('temporal upload ticket missing');
  return uploadTransport.ticket;
};

/** The stated expiry is inside the margin, or already past (a replayed pin). */
const isTicketExpiring = (ticket: TemporalUploadTicket): boolean =>
  ticket.expiresAt - TICKET_REFRESH_MARGIN_MS <= Date.now();

/** Re-mints inside the margin; a failed mint keeps a ticket that is still valid. */
const renewExpiringTicket = async (
  sessionId: string,
  recordingId: string,
  transport: TemporalUploadTransport,
  signal?: AbortSignal
): Promise<TemporalUploadTransport> => {
  const ticket = requireTicket(transport);
  if (!isTicketExpiring(ticket)) return transport;
  try {
    return await refreshTemporalTransport(
      sessionId,
      recordingId,
      transport,
      'expiring',
      signal
    );
  } catch (err) {
    if (err instanceof TransportPinChangedError || ticket.expiresAt <= Date.now())
      throw err;
    logger.warn({
      tag: 'v3_ticket_refresh_failed',
      event_type: 'V3_TICKET_REFRESH_FAILED',
      details: {
        session_id: sessionId,
        recording_id: recordingId,
        delivery_id: transport.deliveryId,
        error_type: err instanceof Error ? err.name : 'UnknownError',
      },
    });
    return transport;
  }
};

const S3_ERROR_CODE = /<Code>([A-Za-z]+)<\/Code>/;
const TOKEN_REJECTION_CODES = new Set(['ExpiredToken', 'InvalidToken']);

/**
 * 403: the policy expired. 400 ExpiredToken/InvalidToken: the session token
 * under the signature expired first. An unreadable 400 body counts
 * as the token case — a wasted mint is cheaper than a stranded chunk.
 */
const isTicketRejection = (err: unknown): boolean => {
  if (!(err instanceof AxiosError)) return false;
  const status = err.response?.status;
  if (status === 403) return true;
  if (status !== 400) return false;
  const body: unknown = err.response?.data;
  const code = typeof body === 'string' ? S3_ERROR_CODE.exec(body)?.[1] : undefined;
  return code === undefined || TOKEN_REJECTION_CODES.has(code);
};

const postSignedForm = async (
  ticket: TemporalUploadTicket,
  objectName: string,
  extraFields: Record<string, string>,
  file: Blob | undefined,
  signal?: AbortSignal
): Promise<void> => {
  const formData = new FormData();
  // Only the fields each object actually has: the policy's starts-with
  // conditions constrain submitted fields but never require them, and the
  // consumer reads absence as absence (country omitted = unknown).
  const fields = {
    ...ticket.fields,
    ...extraFields,
    'Content-Type': file?.type ?? '',
  };
  for (const [name, value] of Object.entries(fields)) {
    if (name === 'key') continue; // replaced with the real object key below
    formData.append(name, value);
  }
  formData.append('key', `${ticket.keyPrefix}${objectName}`);
  // The file field must be last: the upload target ignores fields after it.
  formData.append('file', file ?? new Blob([]), objectName);

  // Bare axios — the signed fields are the credential, no auth header.
  await (signal
    ? axios.post(ticket.url, formData, { signal, timeout: SIGNED_FORM_TIMEOUT_MS })
    : axios.post(ticket.url, formData, { timeout: SIGNED_FORM_TIMEOUT_MS }));
};

type ResponseStream = {
  sessionId: string;
  recordingId: string;
  deliveryId: string;
  startedAt: number;
  userId: string;
  controller: AbortController;
  settledIndices: Set<number>;
  uploadedIndices: Set<number>;
  persistedIndices: Set<number>;
  transcriptWaitAbandoned: boolean;
  lastChunkIndex: number | null;
  stopAfter: number;
  reconnect?: () => void;
  previewRecoveryNeeded: boolean;
  closing: boolean;
};
const responseStreams = new Map<string, ResponseStream>();
const transcriptProgressListeners = new Set<() => void>();
const notifyTranscriptProgress = () => {
  for (const listener of transcriptProgressListeners) listener();
};
const responseStreamKey = (
  stream: Pick<ResponseStream, 'sessionId' | 'recordingId' | 'deliveryId'>
) => JSON.stringify([stream.sessionId, stream.recordingId, stream.deliveryId]);

const closeCompletedResponseStream = (stream: ResponseStream) => {
  if (
    stream.lastChunkIndex === null ||
    stream.closing ||
    stream.controller.signal.aborted
  )
    return;
  for (let index = 0; index <= stream.lastChunkIndex; index += 1) {
    if (!stream.settledIndices.has(index)) return;
  }
  const lastChunkIndex = stream.lastChunkIndex;
  stream.closing = true;
  void refreshCommittedTranscript(stream.sessionId).then((refreshed) => {
    stream.closing = false;
    if (stream.lastChunkIndex !== lastChunkIndex) closeCompletedResponseStream(stream);
    else if (refreshed) stream.controller.abort();
    else stream.reconnect?.();
  });
};

const startChunkResponses = (
  sessionId: string,
  recordingId: string,
  transport: TemporalUploadTransport
): ResponseStream => {
  const key = responseStreamKey({
    sessionId,
    recordingId,
    deliveryId: transport.deliveryId,
  });
  const existing = responseStreams.get(key);
  if (existing && !existing.controller.signal.aborted) return existing;
  const stream: ResponseStream = {
    sessionId,
    recordingId,
    deliveryId: transport.deliveryId,
    startedAt: transport.startedAt,
    userId: getCurrentUser()?.kinde_user_id ?? '',
    controller: new AbortController(),
    settledIndices: new Set(),
    uploadedIndices: new Set(),
    persistedIndices: new Set(),
    transcriptWaitAbandoned: false,
    lastChunkIndex: null,
    previewRecoveryNeeded: false,
    closing: false,
    stopAfter: Date.now() + 12 * 60 * 60_000,
  };
  responseStreams.set(key, stream);
  const unsubscribe = reactQueryClient.getQueryCache().subscribe((event) => {
    if (event.type === 'removed' && getCurrentUser()?.kinde_user_id !== stream.userId) {
      clearChunkResponsePreviews(stream.userId);
      stream.controller.abort();
      return;
    }

    if (
      event.type === 'removed' &&
      isChunkResponsePreviewQuery(
        event.query.queryKey as readonly unknown[],
        stream.userId,
        sessionId
      )
    )
      stream.previewRecoveryNeeded = true;
    if (
      event.type === 'observerAdded' &&
      stream.previewRecoveryNeeded &&
      !stream.closing
    ) {
      const queryKey = event.query.queryKey as readonly unknown[];
      if (
        queryKey[0] === TRANSCRIPTION_SERVICE_TRANSCRIPTS_QUERY_KEY &&
        queryKey[1] === sessionId
      ) {
        stream.previewRecoveryNeeded = false;
        stream.reconnect?.();
      }
    }
  });
  void runChunkResponseStream(stream).finally(() => {
    unsubscribe();
    if (responseStreams.get(key) === stream) responseStreams.delete(key);
    notifyTranscriptProgress();
  });
  return stream;
};

const handleChunkResponseMessage = (
  stream: ResponseStream,
  event: { event: string; data: string }
): void => {
  if (getCurrentUser()?.kinde_user_id !== stream.userId) {
    stream.controller.abort();
    return;
  }
  if (event.event === 'denied') {
    clearChunkResponsePreviews(stream.userId, stream.sessionId);
    stream.controller.abort();
    return;
  }
  if (event.event === 'ready') {
    stream.previewRecoveryNeeded = false;
    void refreshCommittedTranscript(stream.sessionId);
    return;
  }
  if (event.event === 'error') throw new Error('Chunk response stream unavailable');
  if (event.event !== 'chunk' && event.event !== 'delivery') return;
  let message;
  try {
    message =
      event.event === 'delivery'
        ? {
            type: 'delivery' as const,
            result: DeliveryProgressSchema.parse(JSON.parse(event.data)),
          }
        : {
            type: 'chunk' as const,
            result: ChunkResponseSchema.parse(JSON.parse(event.data)),
          };
  } catch {
    logger.warn('chunk_response_invalid_message');
    return;
  }
  const { type, result } = message;
  if (
    result.session_id !== stream.sessionId ||
    result.recording_id !== stream.recordingId ||
    result.delivery_id !== stream.deliveryId
  ) {
    logger.warn('chunk_response_scope_mismatch');
    stream.reconnect?.();
    return;
  }
  if (type === 'delivery') {
    // The service leaves diarisation-pending chunks out of committed_indices, so a
    // listed index is settled even when its terminal chunk event was missed.
    for (const index of result.committed_indices) {
      stream.settledIndices.add(index);
      stream.persistedIndices.add(index);
    }
    if (result.last_chunk_index !== null && stream.lastChunkIndex !== null) {
      stream.lastChunkIndex = Math.max(stream.lastChunkIndex, result.last_chunk_index);
    }
    notifyTranscriptProgress();
    closeCompletedResponseStream(stream);
  } else if (result.state === 'persisted') {
    recordPersistedChunkResponse(stream.userId, result, stream.startedAt);
    if (result.diarisation_status !== 'pending')
      stream.settledIndices.add(result.chunk_index);
    stream.persistedIndices.add(result.chunk_index);
    notifyTranscriptProgress();
    closeCompletedResponseStream(stream);
  } else if (!stream.settledIndices.has(result.chunk_index)) {
    recordChunkResponsePreview(stream.userId, result, stream.startedAt);
  }
};

const runChunkResponseStream = async (stream: ResponseStream): Promise<void> => {
  const { signal } = stream.controller;
  while (!signal.aborted && Date.now() < stream.stopAfter) {
    if (!stream.userId || getCurrentUser()?.kinde_user_id !== stream.userId) break;
    const baseUrl = getTranscriptionBaseUrl();
    if (!baseUrl) break;
    const attempt = new AbortController();
    const abortAttempt = () => attempt.abort();
    stream.reconnect = abortAttempt;
    signal.addEventListener('abort', abortAttempt, { once: true });
    let heartbeatTimer = setTimeout(abortAttempt, 45_000);
    const touch = () => {
      clearTimeout(heartbeatTimer);
      heartbeatTimer = setTimeout(abortAttempt, 45_000);
    };
    try {
      await fetchEventSource(`${baseUrl.replace(/\/$/, '')}/chunk-responses`, {
        method: 'POST',
        headers: buildScribeSseHeaders(getAuthTokenForHttpClientSync()),
        body: JSON.stringify({
          session_id: stream.sessionId,
          recording_id: stream.recordingId,
          delivery_id: stream.deliveryId,
        }),
        signal: attempt.signal,
        openWhenHidden: true,
        onopen: (response) => {
          touch();
          if (response.status === 403 || response.status === 404) {
            clearChunkResponsePreviews(stream.userId, stream.sessionId);
            stream.controller.abort();
            return Promise.resolve();
          }
          if (
            !response.ok ||
            !response.headers.get('content-type')?.includes('text/event-stream')
          ) {
            throw new Error('Chunk response stream unavailable');
          }
          return Promise.resolve();
        },
        onmessage: (event) => {
          touch();
          handleChunkResponseMessage(stream, event);
        },
        onerror: (error) => {
          throw error;
        },
      });
    } catch {
      if (!attempt.signal.aborted) logger.warn('chunk_response_stream_unavailable');
    } finally {
      stream.reconnect = undefined;
      clearTimeout(heartbeatTimer);
      signal.removeEventListener('abort', abortAttempt);
    }
    if (!signal.aborted) {
      await new Promise<void>((resolve) => {
        const done = () => {
          clearTimeout(timer);
          signal.removeEventListener('abort', done);
          resolve();
        };
        const timer = setTimeout(done, 1000 + Math.random() * 4000);
        signal.addEventListener('abort', done, { once: true });
      });
    }
  }
  if (
    !signal.aborted &&
    Date.now() >= stream.stopAfter &&
    getCurrentUser()?.kinde_user_id === stream.userId
  )
    void refreshCommittedTranscript(stream.sessionId);
};

const refreshCommittedTranscript = async (sessionId: string): Promise<boolean> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      reactQueryClient.invalidateQueries(
        { queryKey: [TRANSCRIPTION_SERVICE_TRANSCRIPTS_QUERY_KEY, sessionId] },
        { throwOnError: true }
      ),
      new Promise<never>((_, reject) => {
        timer = setTimeout(
          () => reject(new Error('Transcript refresh timed out')),
          TRANSCRIPT_REFRESH_TIMEOUT_MS
        );
      }),
    ]);
    return true;
  } catch {
    logger.warn('chunk_response_history_refresh_failed');
    return false;
  } finally {
    clearTimeout(timer);
  }
};

const TRANSCRIPT_WAIT_STALL_MS = 10_000;
const TRANSCRIPT_WAIT_CAP_MS = 30_000;

// eslint-disable-next-line @heidi/rules/no-new-carved-domain-data -- Not session data: in-memory counts over this module's private v3 response streams (uploaded vs persisted chunk indices). No session record, key or API call; it cannot leave this module without moving the v3 transport.
export interface TemporalTranscriptProgress {
  uploaded: number;
  persisted: number;
}

export const getTemporalTranscriptProgress = (
  sessionId: string
): TemporalTranscriptProgress => {
  let uploaded = 0;
  let persisted = 0;
  for (const stream of responseStreams.values()) {
    if (
      stream.sessionId !== sessionId ||
      stream.controller.signal.aborted ||
      stream.transcriptWaitAbandoned
    )
      continue;
    for (const index of stream.uploadedIndices) {
      uploaded += 1;
      if (stream.persistedIndices.has(index)) persisted += 1;
    }
  }
  return { uploaded, persisted };
};

// A stream the gate gave up on stops holding later notes until it uploads again.
const abandonTranscriptWait = (sessionId: string) => {
  for (const stream of responseStreams.values()) {
    if (stream.sessionId === sessionId) stream.transcriptWaitAbandoned = true;
  }
};

type TemporalTranscriptWaitOutcome =
  | 'skipped'
  | 'complete'
  | 'stalled'
  | 'capped'
  | 'stream_closed';

interface TemporalTranscriptWaitResult extends TemporalTranscriptProgress {
  outcome: TemporalTranscriptWaitOutcome;
  waitedMs: number;
}

const transcriptWaitAbortError = () => {
  const error = new Error('Waiting for temporal transcripts was aborted');
  error.name = 'AbortError';
  return error;
};

/** Never rejects except on abort; every other end lets the note generate. */
export const waitForTemporalTranscripts = (
  sessionId: string,
  signal?: AbortSignal
): Promise<TemporalTranscriptWaitResult> =>
  new Promise((resolve, reject) => {
    const startedAt = Date.now();
    let last = getTemporalTranscriptProgress(sessionId);
    let stallTimer: ReturnType<typeof setTimeout> | undefined;

    const result = (
      outcome: TemporalTranscriptWaitOutcome,
      progress = last
    ): TemporalTranscriptWaitResult => ({
      outcome,
      waitedMs: Date.now() - startedAt,
      ...progress,
    });

    if (last.uploaded === 0) {
      resolve(result('skipped'));
      return;
    }
    if (last.persisted === last.uploaded) {
      resolve(result('complete'));
      return;
    }
    if (signal?.aborted) {
      reject(transcriptWaitAbortError());
      return;
    }

    const settle = (done: () => void) => {
      clearTimeout(stallTimer);
      clearTimeout(capTimer);
      transcriptProgressListeners.delete(onProgress);
      signal?.removeEventListener('abort', onAbort);
      done();
    };
    const end = (outcome: TemporalTranscriptWaitOutcome, progress = last) =>
      settle(() => {
        if (outcome === 'stalled' || outcome === 'capped')
          abandonTranscriptWait(sessionId);
        resolve(result(outcome, progress));
      });
    const armStall = () => {
      clearTimeout(stallTimer);
      stallTimer = setTimeout(() => end('stalled'), TRANSCRIPT_WAIT_STALL_MS);
    };
    function onProgress() {
      const progress = getTemporalTranscriptProgress(sessionId);
      // The stream ended unfinished (denied, signed out, expired): nothing more
      // can arrive, so waiting on would only spend the cap.
      if (progress.uploaded === 0) {
        end('stream_closed');
        return;
      }
      if (progress.persisted === progress.uploaded) {
        end('complete', progress);
        return;
      }
      if (progress.persisted > last.persisted) armStall();
      last = progress;
    }
    function onAbort() {
      settle(() => reject(transcriptWaitAbortError()));
    }

    const capTimer = setTimeout(() => end('capped'), TRANSCRIPT_WAIT_CAP_MS);
    armStall();
    transcriptProgressListeners.add(onProgress);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
